export const slugify = (value: string): string => {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9- ]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-+|-+$/g, "");
};

export const formatPhoneNumberFromE164 = (phoneNumber: string) => {
    const regex = /^\+[1-9]\d{1,14}$/;
    if (!regex.test(phoneNumber)) {
        throw new Error("Invalid E.164 phone number");
    }
    return `(${phoneNumber.slice(2, 5)}) ${phoneNumber.slice(
        5,
        8
    )}-${phoneNumber.slice(8)}`;
};

/**
 * Formats a location query string by capitalizing city names.
 * Note: This helper utility is not perfect and may not handle all location name formats correctly.
 * For public-facing pages, consider using an API such as Google Places for accurate location formatting.
 * Intended use is on internal admin pages to make location strings look nicer.
 */
export const formatLocationQueryString = (
    locationQueryString: string
): string => {
    if (!locationQueryString) {
        return locationQueryString;
    }

    const parts = locationQueryString.split(",").map((part) => part.trim());

    // Capitalize each word in the city name, preserving special characters
    const capitalizeWord = (word: string): string => {
        if (!word) return word;
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    };

    // Split by spaces and hyphens, capitalize each part, then rejoin
    const formatCity = (city: string): string => {
        return city
            .split(/([\s-]+)/)
            .map((part) => {
                // Preserve separators (spaces and hyphens) as-is
                if (/^[\s-]+$/.test(part)) {
                    return part;
                }
                return capitalizeWord(part);
            })
            .join("");
    };

    // Handle case with no comma - just format the city name
    if (parts.length === 1) {
        return formatCity(parts[0]);
    }

    // Handle case with exactly one comma - format "city, province"
    if (parts.length === 2) {
        const [city, province] = parts;
        return `${formatCity(city)}, ${province.toUpperCase()}`;
    }

    // Multiple commas - return original string
    return locationQueryString;
};
